import {
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Input,
  Spinner,
  Stack,
  Tag,
  Text,
  Textarea,
  useBreakpointValue,
  useToast,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { ClipboardEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiCopy, FiSend } from "react-icons/fi";
import {
  EmailInviteLanguage,
  EmailInviteStatusDto,
  type PortalLinkShareDto,
  useListPortalLinkSharesQuery,
  useSharePortalLinkMutation,
} from "../../autogen/bff-api";
import { useApiError } from "../../common/errors/useApiError";
import { displayDateWithTime } from "../../common/formatting/displayDateWithTime";
import { readClipboardHtmlTable } from "../../common/paste/clipboardTable";
import { EmaiInviteStatusTag } from "./EmailInviteStatusTag";
import { findEmailAddresses, tableAsText } from "./findEmailAddresses";

export const MAX_RECIPIENTS_PER_SEND = 200;

export const SupplierPortalLinkSection = ({
  url,
  eventId,
  maySend,
}: {
  url: string;
  eventId: string;
  maySend: boolean;
}) => {
  const { t } = useTranslation();
  const toast = useToast();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: t("Link copied"), status: "success", duration: 3000 });
    } catch {
      toast({
        title: t(
          "The link could not be copied. Select it and copy it yourself.",
        ),
        status: "error",
      });
    }
  };

  return (
    <Stack spacing="4">
      <Text fontWeight="600">{t("Suppliers")}</Text>
      <Text fontSize="sm" color="smMuted">
        {t(
          "Anyone with the link can see the procurement in the supplier portal without logging in.",
        )}
      </Text>
      <Stack direction={{ base: "column", sm: "row" }}>
        <Input
          flex={{ base: "none", sm: "1" }}
          minW="0"
          value={url}
          isReadOnly
          aria-label={t("Link to the procurement in the supplier portal") ?? ""}
          onFocus={(e) => e.target.select()}
        />
        <Button
          colorScheme="teal"
          leftIcon={<Icon as={FiCopy} />}
          onClick={copy}
          flexShrink={0}
          alignSelf={{ base: "flex-start", sm: "auto" }}
        >
          {t("Copy link")}
        </Button>
      </Stack>
      {maySend && <SendPortalLink eventId={eventId} />}
      <SentPortalLinks eventId={eventId} />
    </Stack>
  );
};

const SendPortalLink = ({ eventId }: { eventId: string }) => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { trigger } = useApiError();
  const [text, setText] = useState("");
  const [share, { isLoading }] = useSharePortalLinkMutation();
  const { addresses, unreadable } = findEmailAddresses(text);
  const tooMany = addresses.length > MAX_RECIPIENTS_PER_SEND;

  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    if (!e.clipboardData.types.includes("text/html")) return;
    try {
      const pasted = tableAsText(readClipboardHtmlTable(e.clipboardData));
      if (!pasted.trim()) return;
      e.preventDefault();
      setText((current) => (current.trim() ? `${current}\n${pasted}` : pasted));
    } catch {
      return;
    }
  };

  const send = async () => {
    try {
      await share({
        eventId,
        sharePortalLinkRequest: {
          emails: addresses,
          language: i18n.language.startsWith("en")
            ? EmailInviteLanguage.En
            : EmailInviteLanguage.No,
        },
      }).unwrap();
      toast({
        title: t("The link was sent to {{count}} addresses", {
          count: addresses.length,
        }),
        description:
          unreadable.length > 0
            ? t("{{count}} were not sent", { count: unreadable.length })
            : undefined,
        status: "success",
        duration: 4000,
      });
      setText(unreadable.join("\n"));
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <Stack spacing="2">
      <FormControl>
        <FormLabel>{t("Send the link by email")}</FormLabel>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onPaste={onPaste}
          rows={4}
          placeholder={
            t("Paste email addresses, for example cells copied from Excel") ??
            ""
          }
        />
      </FormControl>
      {text.trim() && (
        <Text fontSize="sm" color="smMuted">
          {t("{{count}} email addresses found", { count: addresses.length })}
        </Text>
      )}
      {addresses.length > 0 && (
        <Wrap
          spacing="1"
          maxH="32"
          overflowY="auto"
          aria-label={t("Addresses that will get the link") ?? ""}
        >
          {addresses.map((address) => (
            <WrapItem key={address}>
              <Tag size="sm">{address}</Tag>
            </WrapItem>
          ))}
        </Wrap>
      )}
      {unreadable.length > 0 && (
        <Stack spacing="0">
          <Text fontSize="sm" color="smWarning">
            {t("Not read as an email address:")}
          </Text>
          {unreadable.map((entry, i) => (
            <Text key={`${entry}-${i}`} fontSize="sm" color="smMuted">
              {entry}
            </Text>
          ))}
        </Stack>
      )}
      {tooMany && (
        <Text fontSize="sm" color="smError">
          {t("You can send to at most {{max}} addresses at a time.", {
            max: MAX_RECIPIENTS_PER_SEND,
          })}
        </Text>
      )}
      <HStack>
        <Button
          leftIcon={<Icon as={FiSend} />}
          colorScheme="teal"
          onClick={send}
          isLoading={isLoading}
          isDisabled={addresses.length === 0 || tooMany}
        >
          {t("Send link")}
        </Button>
      </HStack>
    </Stack>
  );
};

const SentPortalLinks = ({ eventId }: { eventId: string }) => {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useListPortalLinkSharesQuery({
    eventId,
  });
  const [showAll, setShowAll] = useState(false);
  const shares = failedFirstThenNewest(data?.shares ?? []);
  const collapsedRows =
    useBreakpointValue(COLLAPSED_ROWS, { ssr: false }) ?? COLLAPSED_ROWS.sm;
  const shown = showAll ? shares : shares.slice(0, collapsedRows);
  const failed = shares.filter(
    (s) => s.status === EmailInviteStatusDto.SendingFailed,
  ).length;
  const opened = shares.filter(
    (s) => s.status === EmailInviteStatusDto.Opened,
  ).length;
  return (
    <Stack spacing="2">
      <Divider />
      <Text fontSize="sm" fontWeight="600">
        {t("The link was sent to")}
      </Text>
      {shares.length > 0 && (
        <HStack spacing="1" fontSize="sm" color="smMuted" flexWrap="wrap">
          <Text>{t("{{n}} sent", { n: shares.length })}</Text>
          {failed > 0 && (
            <>
              <Text aria-hidden>·</Text>
              <Text color="smError">{t("{{n}} failed", { n: failed })}</Text>
            </>
          )}
          {opened > 0 && (
            <>
              <Text aria-hidden>·</Text>
              <Text>{t("{{n}} opened", { n: opened })}</Text>
            </>
          )}
        </HStack>
      )}
      {isLoading && <Spinner size="sm" aria-label={t("Loading") ?? ""} />}
      {isError && (
        <HStack>
          <Text fontSize="sm" color="smError">
            {t("Could not load who the link was sent to.")}
          </Text>
          <Button size="xs" variant="outline" onClick={() => refetch()}>
            {t("Try again")}
          </Button>
        </HStack>
      )}
      {!isLoading && !isError && shares.length === 0 && (
        <Text fontSize="sm" color="smMuted">
          {t("The link has not been sent to anyone yet.")}
        </Text>
      )}
      {shown.map((share) => (
        <Stack
          key={share.email}
          direction={{ base: "column", sm: "row" }}
          justify="space-between"
          align="flex-start"
          spacing={{ base: "1", sm: "2" }}
        >
          <Stack spacing="0" minW="0" flex="1">
            <Text wordBreak="break-all">{share.email}</Text>
            <Text fontSize="xs" color="smMuted">
              {t("Sent {{date}} by {{name}}", {
                date: displayDateWithTime(share.sentAt, t("at")),
                name: share.sentBy,
              })}
            </Text>
          </Stack>
          <Box flexShrink={0} whiteSpace="nowrap">
            <EmaiInviteStatusTag
              status={share.status}
              openedAt={share.openedAt ?? undefined}
              size="sm"
              sentLabel={t("Link sent") ?? undefined}
              failedExplanation={
                t(
                  "The email could not be delivered. Check the address, then paste it above and send the link again.",
                ) ?? undefined
              }
            />
          </Box>
        </Stack>
      ))}
      {shares.length > collapsedRows && (
        <Button
          size="sm"
          variant="link"
          colorScheme="teal"
          alignSelf="flex-start"
          onClick={() => setShowAll((current) => !current)}
        >
          {showAll ? t("Hide") : t("Show all ({{n}})", { n: shares.length })}
        </Button>
      )}
    </Stack>
  );
};

const COLLAPSED_ROWS = { base: 2, sm: 5 };

const failedFirstThenNewest = (
  shares: PortalLinkShareDto[],
): PortalLinkShareDto[] =>
  [...shares].sort((a, b) => {
    const aFailed = a.status === EmailInviteStatusDto.SendingFailed;
    const bFailed = b.status === EmailInviteStatusDto.SendingFailed;
    if (aFailed !== bFailed) return aFailed ? -1 : 1;
    return b.sentAt.localeCompare(a.sentAt);
  });
